/**
 * Privacy Policy Page
 * Privacyverklaring conform AVG / GDPR voor SuperKonnected
 * Versie 1.0 | Laatst bijgewerkt: 19 februari 2026
 * 
 * Styled with SuperKonnected brand identity:
 * - Sage green (#8BA59B)
 * - Warm beige/latte background
 * - Glassmorphism effects
 * - Inter font family
 */

import React from 'react';
import { Shield, Lock, Mail, Globe, FileText, AlertCircle, Building2, Users } from 'lucide-react';
import PageHeader from './ui/PageHeader';
import TranslateButton from './ui/TranslateButton';

// Brand colors
const colors = {
  sage: '#8BA59B',
  sageLight: 'rgba(139, 165, 155, 0.15)',
  textPrimary: 'rgba(60, 55, 50, 0.92)',
  textSecondary: 'rgba(80, 75, 70, 0.85)',
  textTertiary: 'rgba(100, 95, 90, 0.7)',
  textMuted: 'rgba(100, 95, 90, 0.6)',
  border: 'rgba(139, 165, 155, 0.2)',
  cardBg: 'rgba(248, 246, 243, 0.95)',
  sectionBg: 'rgba(248, 246, 243, 0.5)',
};

interface PrivacyPolicyProps {
  embedded?: boolean;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ embedded = false }) => {
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    height: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    background: 'linear-gradient(135deg, #E8E6E1 0%, #D8D6D1 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    padding: '40px 20px',
  };

  const cardStyle: React.CSSProperties = {
    background: colors.cardBg,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
    maxWidth: '900px',
    margin: '0 auto',
  };

  const iconBoxStyle: React.CSSProperties = {
    width: '40px',
    height: '40px',
    background: colors.sageLight,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: `1px solid ${colors.border}`,
  };

  const headingStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '16px',
  };

  const textStyle: React.CSSProperties = {
    fontSize: '15px',
    lineHeight: '1.7',
    color: colors.textSecondary,
    marginBottom: '12px',
  };

  const highlightBoxStyle: React.CSSProperties = {
    background: colors.sectionBg,
    borderRadius: '16px',
    padding: '20px',
    marginTop: '16px',
    border: `1px solid ${colors.border}`,
  };

  const inner = (
      <div style={embedded ? { ...cardStyle, maxWidth: '100%', margin: 0, borderRadius: 0, boxShadow: 'none', border: 'none', padding: '24px' } : cardStyle}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px', paddingBottom: '32px', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ 
            display: 'inline-flex',
            alignItems: 'center', 
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            background: colors.sageLight,
            borderRadius: '50%',
            marginBottom: '20px'
          }}>
            <Shield style={{ width: '32px', height: '32px', color: colors.sage }} />
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: '16px', color: colors.textSecondary, marginBottom: '4px' }}>
            Privacyverklaring conform AVG / GDPR
          </p>
          <p style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '16px' }}>
            Versie 1.0 | Laatst bijgewerkt: 19 februari 2026
          </p>
          <TranslateButton />
        </div>

        {/* Introduction */}
        <div style={sectionStyle}>
          <p style={textStyle}>
            SuperKonnected, onderdeel van Business Security B.V., hecht grote waarde aan de privacy van haar gebruikers. 
            In deze Privacy Policy leggen wij uit welke persoonsgegevens wij verzamelen, waarom wij deze gebruiken, 
            hoe wij deze beschermen en welke rechten jij als gebruiker hebt. Wij verwerken persoonsgegevens conform de 
            Algemene Verordening Gegevensbescherming (AVG/GDPR).
          </p>
        </div>

        {/* 1. Verwerkingsverantwoordelijke */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Building2 style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>1. Verwerkingsverantwoordelijke</h2>
              <p style={textStyle}>De verwerkingsverantwoordelijke voor jouw persoonsgegevens is:</p>
              <div style={highlightBoxStyle}>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>Bedrijfsnaam:</strong> Business Security B.V.</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>Merknaam:</strong> SuperKonnected</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>KvK-nummer:</strong> 91885582</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>BTW-nummer:</strong> NL865 804 515 B01</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>Adres:</strong> Rontgenweg 2A, 8503 AG Joure, Nederland</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>E-mail:</strong> support@superkonnected.com</p>
                <p style={{ ...textStyle, marginBottom: 0 }}><strong>Website:</strong> <a href="https://superkonnected.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com</a></p>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Welke Persoonsgegevens */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <FileText style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>2. Welke Persoonsgegevens Verzamelen Wij</h2>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.1 Accountgegevens (via Google OAuth)</p>
                <p style={textStyle}>Bij het aanmaken van een account via Google OAuth ontvangen wij de volgende gegevens van Google:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li>Naam</li>
                  <li>E-mailadres</li>
                  <li>Profielfoto</li>
                  <li>Google gebruikers-ID</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.2 LinkedIn Integratie</p>
                <p style={textStyle}>Als je kiest voor LinkedIn integratie, ontvangen wij:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li>LinkedIn profielnaam</li>
                  <li>LinkedIn e-mailadres</li>
                  <li>LinkedIn profielfoto</li>
                  <li>LinkedIn gebruikers-ID</li>
                  <li>LinkedIn access token (versleuteld opgeslagen)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.3 Facebook Integratie</p>
                <p style={textStyle}>Als je kiest voor Facebook integratie, ontvangen wij:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li>Facebook profielnaam</li>
                  <li>Facebook e-mailadres</li>
                  <li>Facebook profielfoto</li>
                  <li>Facebook gebruikers-ID</li>
                  <li>Facebook access token (versleuteld opgeslagen)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.4 Instagram Integratie</p>
                <p style={textStyle}>Als je kiest voor Instagram integratie, ontvangen wij:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li>Instagram gebruikersnaam</li>
                  <li>Instagram profielfoto</li>
                  <li>Instagram gebruikers-ID</li>
                  <li>Instagram access token (versleuteld opgeslagen)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.5 X / Twitter Integratie</p>
                <p style={textStyle}>Als je kiest voor X (voorheen Twitter) integratie, ontvangen wij:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li>X gebruikersnaam en weergavenaam</li>
                  <li>X profielfoto</li>
                  <li>X gebruikers-ID</li>
                  <li>X access token (versleuteld opgeslagen)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.6 Door Jou Geplaatste Content</p>
                <p style={textStyle}>Wij slaan op wat jij actief op het Platform plaatst, waaronder berichten, foto's, video's, reacties en profielinformatie.</p>
              </div>

              <div>
                <p style={{ ...textStyle, fontWeight: 600 }}>2.7 Technische en Gebruiksgegevens</p>
                <p style={textStyle}>Wij verzamelen automatisch bepaalde technische gegevens bij gebruik van het Platform:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li>IP-adres</li>
                  <li>Browsertype en -versie</li>
                  <li>Apparaattype en besturingssysteem</li>
                  <li>Datum en tijdstip van bezoek</li>
                  <li>Bezochte pagina's en klikgedrag</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Doeleinden */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Doeleinden van de Verwerking</h2>
          <p style={textStyle}>Wij gebruiken jouw persoonsgegevens uitsluitend voor de volgende doeleinden:</p>
          <ul style={{ marginLeft: '20px', ...textStyle }}>
            <li><strong>Accountbeheer:</strong> het aanmaken, beheren en beveiligen van jouw account.</li>
            <li><strong>Dienstverlening:</strong> het aanbieden van alle functies van het Platform.</li>
            <li><strong>Communicatie:</strong> het sturen van serviceberichten, updates en (indien toestemming gegeven) nieuwsbrieven.</li>
            <li><strong>Social media integratie:</strong> het posten van content namens jou op gekoppelde platformen (alleen na expliciete toestemming).</li>
            <li><strong>Beveiliging:</strong> het detecteren en voorkomen van fraude, misbruik en ongeautoriseerde toegang.</li>
            <li><strong>Verbetering:</strong> het analyseren van gebruik om het Platform te verbeteren.</li>
            <li><strong>Wettelijke verplichtingen:</strong> het nakomen van wettelijke verplichtingen.</li>
          </ul>
        </div>

        {/* 4. Rechtsgrondslag */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Rechtsgrondslag van de Verwerking</h2>
          <p style={textStyle}>Wij verwerken jouw persoonsgegevens op basis van de volgende rechtsgrondslagen:</p>
          <ul style={{ marginLeft: '20px', ...textStyle }}>
            <li><strong>Uitvoering van een overeenkomst:</strong> voor het leveren van de diensten waarvoor je je hebt geregistreerd.</li>
            <li><strong>Toestemming:</strong> voor social media integraties en marketingcommunicatie. Je kunt toestemming te allen tijde intrekken.</li>
            <li><strong>Gerechtvaardigd belang:</strong> voor beveiliging en fraudepreventie, mits jouw belangen niet zwaarder wegen.</li>
            <li><strong>Wettelijke verplichting:</strong> wanneer de wet ons verplicht gegevens te bewaren of te verstrekken.</li>
          </ul>
        </div>

        {/* 5. Bewaartermijnen */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Bewaartermijnen</h2>
          <p style={textStyle}>Wij bewaren jouw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor zij zijn verzameld:</p>
          <ul style={{ marginLeft: '20px', ...textStyle }}>
            <li><strong>Accountgegevens:</strong> zolang je account actief is, plus maximaal 12 maanden na verwijdering.</li>
            <li><strong>Access tokens (social media):</strong> maximaal 90 dagen na intrekking van de koppeling.</li>
            <li><strong>Technische loggegevens:</strong> maximaal 12 maanden.</li>
            <li><strong>Financiële gegevens:</strong> 7 jaar conform de wettelijke bewaarplicht.</li>
          </ul>
        </div>

        {/* 6. Beveiliging */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Lock style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>6. Beveiliging van Persoonsgegevens</h2>
              <p style={textStyle}>
                SuperKonnected neemt passende technische en organisatorische maatregelen om jouw persoonsgegevens 
                te beschermen tegen verlies, misbruik en ongeautoriseerde toegang:
              </p>
              <ul style={{ marginLeft: '20px', ...textStyle }}>
                <li>Versleuteling van access tokens en gevoelige gegevens.</li>
                <li>Beveiligde HTTPS verbindingen voor alle datatransmissie.</li>
                <li>Firebase backend (Google Cloud infrastructure) met hoge beveiligingsstandaarden.</li>
                <li>Beperkte toegang tot persoonsgegevens, uitsluitend voor medewerkers die deze nodig hebben.</li>
                <li>Regelmatige beveiligingscontroles en updates.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 7. Delen met Derden */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Users style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>7. Delen van Gegevens met Derden</h2>
              <p style={textStyle}>SuperKonnected verkoopt jouw persoonsgegevens nooit aan derden. Wij delen gegevens uitsluitend in de volgende situaties:</p>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>7.1 Verwerkers</p>
                <p style={textStyle}>Wij maken gebruik van de volgende dienstverleners die namens ons persoonsgegevens verwerken:</p>
                <ul style={{ marginLeft: '20px', ...textStyle }}>
                  <li><strong>Google Firebase:</strong> voor opslag van gegevens en authenticatie (Google Cloud, VS, met standaard contractbepalingen conform AVG).</li>
                  <li><strong>Google OAuth:</strong> voor authenticatie via Google-accounts.</li>
                  <li><strong>LinkedIn, Facebook, Instagram, X:</strong> voor social media integraties (alleen na jouw toestemming).</li>
                  <li><strong>Betaalproviders:</strong> voor verwerking van abonnementsbetalingen.</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>7.2 Wettelijke Verplichting</p>
                <p style={textStyle}>Wij kunnen gegevens verstrekken aan bevoegde autoriteiten indien wij daartoe wettelijk verplicht zijn of een gerechtelijk bevel ontvangen.</p>
              </div>

              <div>
                <p style={{ ...textStyle, fontWeight: 600 }}>7.3 Geen Andere Derden</p>
                <p style={textStyle}>Buiten bovenstaande gevallen delen wij jouw persoonsgegevens niet met derden, tenzij je daar uitdrukkelijk toestemming voor hebt gegeven.</p>
              </div>

              <div style={{ marginTop: '20px' }}>
                <p style={{ ...textStyle, fontWeight: 600 }}>7.4 Google Workspace API Data Usage</p>
                <p style={textStyle}>
                  The use of raw or derived user data received from Workspace APIs will adhere to the Google User Data Policy, including the Limited Use requirements.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 8. Internationale Doorgifte */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Globe style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>8. Internationale Doorgifte</h2>
              <p style={textStyle}>
                Sommige van onze dienstverleners (zoals Google Firebase) zijn gevestigd buiten de Europese Economische Ruimte (EER). 
                In dergelijke gevallen zorgen wij voor passende waarborgen conform de AVG, zoals de Standaard Contractbepalingen van de 
                Europese Commissie (Standard Contractual Clauses).
              </p>
            </div>
          </div>
        </div>

        {/* 9. Cookies */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Cookies en Vergelijkbare Technologieën</h2>
          <p style={textStyle}>SuperKonnected maakt gebruik van cookies en vergelijkbare technologieën. Wij onderscheiden:</p>
          <ul style={{ marginLeft: '20px', ...textStyle }}>
            <li><strong>Functionele cookies:</strong> noodzakelijk voor het functioneren van het Platform (geen toestemming vereist).</li>
            <li><strong>Analytische cookies:</strong> voor het meten van het gebruik van het Platform (toestemming vereist).</li>
            <li><strong>Marketingcookies:</strong> voor gepersonaliseerde communicatie (toestemming vereist).</li>
          </ul>
          <p style={textStyle}>
            Bij je eerste bezoek vragen wij toestemming voor niet-functionele cookies. Je kunt je cookievoorkeuren te allen tijde 
            aanpassen via de cookie-instellingen op het Platform. Voor meer informatie zie ons Cookiebeleid op <a href="https://superkonnected.com/cookies" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com/cookies</a>.
          </p>
        </div>

        {/* 10. Jouw Rechten */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Shield style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>10. Jouw Rechten als Betrokkene</h2>
              <p style={textStyle}>Op grond van de AVG heb je de volgende rechten met betrekking tot jouw persoonsgegevens:</p>
              <ul style={{ marginLeft: '20px', ...textStyle }}>
                <li><strong>Recht op inzage:</strong> je kunt opvragen welke gegevens wij van jou verwerken.</li>
                <li><strong>Recht op rectificatie:</strong> je kunt onjuiste gegevens laten corrigeren.</li>
                <li><strong>Recht op verwijdering ('recht op vergetelheid'):</strong> je kunt verzoeken jouw gegevens te verwijderen.</li>
                <li><strong>Recht op beperking van verwerking:</strong> je kunt verzoeken de verwerking tijdelijk te beperken.</li>
                <li><strong>Recht op dataportabiliteit:</strong> je kunt jouw gegevens opvragen in een gestructureerd, veelgebruikt formaat.</li>
                <li><strong>Recht van bezwaar:</strong> je kunt bezwaar maken tegen verwerking op basis van gerechtvaardigd belang.</li>
                <li><strong>Recht om toestemming in te trekken:</strong> voor verwerking op basis van toestemming, kun je deze te allen tijde intrekken.</li>
              </ul>
              <p style={textStyle}>
                Om een verzoek in te dienen, neem je contact op via support@superkonnected.com. Wij reageren binnen 30 dagen. 
                Je hebt ook het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens (www.autoriteitpersoonsgegevens.nl).
              </p>
            </div>
          </div>
        </div>

        {/* 11. Minderjarigen */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Minderjarigen</h2>
          <p style={textStyle}>
            Het Platform is niet bestemd voor personen jonger dan 18 jaar. Wij verzamelen niet bewust persoonsgegevens van minderjarigen. 
            Als wij constateren dat gegevens van een minderjarige zijn verzameld, verwijderen wij deze zo spoedig mogelijk.
          </p>
        </div>

        {/* 12. Wijzigingen */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Wijzigingen in deze Privacy Policy</h2>
          <p style={textStyle}>
            SuperKonnected behoudt zich het recht voor deze Privacy Policy te allen tijde te wijzigen. Belangrijke wijzigingen worden 
            minimaal 30 dagen van tevoren gecommuniceerd via e-mail of via een melding op het Platform. De meest actuele versie is altijd 
            beschikbaar op <a href="https://superkonnected.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com/privacy</a>.
          </p>
        </div>

        {/* 13. Contact */}
        <div style={{ ...highlightBoxStyle, marginBottom: '32px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <Mail style={{ width: '24px', height: '24px', color: colors.sage, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h3 style={{ ...headingStyle, marginBottom: '12px' }}>13. Contact en Vragen</h3>
              <p style={textStyle}>
                Voor vragen, verzoeken of klachten over deze Privacy Policy of de verwerking van jouw persoonsgegevens, neem je contact op via:
              </p>
              <div style={{ ...textStyle, fontSize: '14px' }}>
                <p>📧 E-mail: support@superkonnected.com</p>
                <p>🌐 Website: <a href="https://superkonnected.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com</a></p>
                <p>📮 Post: Rontgenweg 2A, 8503 AG Joure, Nederland</p>
              </div>
              <p style={{ ...textStyle, fontSize: '14px', marginBottom: 0, marginTop: '12px' }}>
                Wij streven ernaar alle verzoeken binnen 30 dagen te beantwoorden.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          paddingTop: '24px', 
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
          fontSize: '13px',
          color: colors.textMuted
        }}>
          <p>SuperKonnected: het eerste Clean Mind social media platform voor ondernemers.</p>
          <p style={{ marginTop: '8px' }}>© 2026 SuperKonnected / Business Security B.V. Alle rechten voorbehouden.</p>
        </div>

        {/* Back Link - hidden when embedded */}
        {!embedded && (
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <button
              onClick={() => window.history.back()}
              style={{
                color: colors.sage,
                fontWeight: 500,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '15px',
                transition: 'opacity 200ms ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              Ga terug
            </button>
          </div>
        )}
      </div>
  );

  if (embedded) return inner;

  return (
    <div style={containerStyle}>
      {inner}
    </div>
  );
};

export default PrivacyPolicy;
